import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

// Rxjs import
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

// 3rd party imports
import { classToPlain, plainToClass } from 'class-transformer';

// Constants imports
import { URLConstants } from '../../../../_constants/url-constants';

// model imports
import { ARAdjustmentParameterDto } from '../../_models/account-receivable/ar-adjustment/ar-adjustment-parameter-dto';
import { ARAdjustmentResultDto } from '../../_models/account-receivable/ar-adjustment/ar-adjustment-result-dto';
import { TransactionLogResultDto } from '../../_models/common/transaction-log-result-dto';
import { ARAdjustmentReverseParameterDto } from '../../_models/account-receivable/ar-adjustment/ar-adjustment-reverse-parameter-dto';
import { ARAdjustmentWriteOffParameterDto } from '../../_models/account-receivable/ar-adjustment/ar-adjustment-writeoff-parameter-dto';
import { TransactionLogParameterDto } from '../../_models/common/transaction-log-parameter-dto';
import { ARAdjustmentEditorParameterDto } from '../../_models/account-receivable/ar-adjustment/ar-adjustment-editor-parameter-dto';
import { ARAdjustmentEditorResultDto } from '../../_models/account-receivable/ar-adjustment/ar-adjustment-editor-result-dto';
import { ARAdjustmentGLAccountLabelsParameterDto } from '../../_models/account-receivable/ar-adjustment/ar-adjustment-gl-account-labels-parameter-dto';
import { GLAccountLabelSelectorResultDto } from '../../_models/common/gl-account/gl-account-label-selector-result-dto';
import { SubCategoryByGlAccountParametersDto } from '../../_models/common/subcategory-by-gl-account-parametere-dto';
import { GLSubcategorySelectorResultDto } from '../../_models/common/gl-account/glsubcategory-selector-result-dto';
import { ARAdjustmentRawDto } from '../../_models/account-receivable/ar-adjustment/ar-adjustment-raw-dto';
import { ARAdjustmentAttachmentUpdateParameterDto } from '../../_models/account-receivable/ar-adjustment/ar-adjustment-attachment-update-parameter-dto';
import { ReverseEditorResultDto } from '../../../../shared-module/_models/reverse-editor-result-dto';

@Injectable({
    providedIn: 'root'
})
export class ARAdjustmentService {
    constructor(private readonly httpClient: HttpClient) {}

    getARAdjustmentDetail(param: ARAdjustmentParameterDto): Observable<ARAdjustmentResultDto> {
        const url = `${URLConstants.ccCBARAdjustmentApiUrl}/detail${param.getQueryString()}`;
        return this.httpClient
            .get<ARAdjustmentResultDto>(url)
            .pipe(map((response: ARAdjustmentResultDto) => plainToClass(ARAdjustmentResultDto, response)));
    }

    getARAdjustmentHistory(baseParam: TransactionLogParameterDto): Observable<TransactionLogResultDto[]> {
        const url = `${URLConstants.ccCBARAdjustmentApiUrl}/history${baseParam.getQueryString()}`;
        return this.httpClient
            .get<TransactionLogResultDto[]>(url)
            .pipe(map((response: TransactionLogResultDto[]) => plainToClass(TransactionLogResultDto, response)));
    }

    getARAdjustmentReverseEditor(baseParam: ARAdjustmentParameterDto): Observable<ReverseEditorResultDto> {
        const url = `${URLConstants.ccCBARAdjustmentApiUrl}/reverse/editor${baseParam.getQueryString()}`;
        return this.httpClient
            .get<ReverseEditorResultDto>(url)
            .pipe(map((response: ReverseEditorResultDto) => plainToClass(ReverseEditorResultDto, response)));
    }

    reverseARAdjustment(params: ARAdjustmentReverseParameterDto): Observable<any> {
        const url = `${URLConstants.ccCBARAdjustmentApiUrl}/reverse`;
        return this.httpClient.put(url, classToPlain(params)).pipe(map((response: any) => response));
    }

    writeOffARAdjustment(params: ARAdjustmentWriteOffParameterDto): Observable<any> {
        const url = `${URLConstants.ccCBARAdjustmentApiUrl}/writeoff`;
        return this.httpClient.put(url, classToPlain(params)).pipe(map((response: any) => response));
    }

    deleteARAdjustment(param: ARAdjustmentParameterDto): Observable<any> {
        const url = URLConstants.ccCBARAdjustmentApiUrl;
        const options = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json'
            }),
            body: classToPlain(param)
        };
        return this.httpClient.delete<any>(url, options);
    }

    getARAdjustmentEditor(param: ARAdjustmentEditorParameterDto): Observable<ARAdjustmentEditorResultDto> {
        const url = `${URLConstants.ccCBARAdjustmentApiUrl}/editor${param.getQueryString()}`;
        return this.httpClient
            .get<ARAdjustmentEditorResultDto>(url)
            .pipe(map((response: ARAdjustmentEditorResultDto) => plainToClass(ARAdjustmentEditorResultDto, response)));
    }

    getARAdjustmentGLAccountLabels(
        param: ARAdjustmentGLAccountLabelsParameterDto
    ): Observable<GLAccountLabelSelectorResultDto[]> {
        const url = `${URLConstants.ccCBARAdjustmentApiUrl}/glaccountlabels${param.getQueryString()}`;
        return this.httpClient
            .get<GLAccountLabelSelectorResultDto[]>(url)
            .pipe(
                map((response: GLAccountLabelSelectorResultDto[]) =>
                    plainToClass(GLAccountLabelSelectorResultDto, response)
                )
            );
    }

    getSubCategories(param: SubCategoryByGlAccountParametersDto): Observable<GLSubcategorySelectorResultDto[]> {
        const url = `${URLConstants.ccCBARAdjustmentApiUrl}/subcategories${param.getQueryString()}`;
        return this.httpClient
            .get<GLSubcategorySelectorResultDto[]>(url)
            .pipe(
                map((response: GLSubcategorySelectorResultDto[]) =>
                    plainToClass(GLSubcategorySelectorResultDto, response)
                )
            );
    }

    addNewARAdjustment(baseParam: ARAdjustmentRawDto): Observable<any> {
        return this.httpClient.post(`${URLConstants.ccCBARAdjustmentApiUrl}`, classToPlain(baseParam));
    }

    editARAdjustment(baseParam: ARAdjustmentRawDto): Observable<any> {
        return this.httpClient.put(`${URLConstants.ccCBARAdjustmentApiUrl}`, classToPlain(baseParam));
    }

    uploadAttachments(baseParam: ARAdjustmentAttachmentUpdateParameterDto): Observable<any> {
        return this.httpClient.put(`${URLConstants.ccCBARAdjustmentApiUrl}/attachment`, classToPlain(baseParam));
    }
}
